import log from "loglevel"
import { create } from "zustand"
import { persist } from "zustand/middleware"
import { API, NexusSuccessResponse } from "../api/API"
import {
    EventRegexExpression,
    EventRegexTemplate,
    FlightlineEvent,
    regexTemplateOptions,
} from "../models/FlightlineEvent"
import { Order, SortOrder } from "../models/SortOrder"
import { getEventsForMission, getFlightPlannedLines } from "../store/actions/MissionsActions"
import { CreatedFlightPlannedLine, FlightplanType } from "../store/types/FlightPlansTypes"
import { FlightlineStatusOption } from "../store/types/MissionsTypes"
import {
    EventPlannedLine,
    getEventPlannedLines,
    parseFlightlineEventNames,
    sortFlightlineEvents,
} from "../utils/FlightlineEventUtils"

interface FlightlineEventState {
    lastSelectedEventId: number
    selectedEventIds: number[]
    flightlineEvents: FlightlineEvent[]
    allFlightplans: FlightplanType[]
    selectedFlightplans: FlightplanType[]
    error: string
    isFetchingEvents: boolean
    isFetchingPlannedLines: boolean
    isFetchingAllFlightPlans: boolean
    missionId?: number
    eventPlannedLines: EventPlannedLine[]
    curRegexExpression: EventRegexExpression
    sortOrder: SortOrder<keyof FlightlineEvent>
    importRange: string
}

interface FlightlineEventActions {
    fetchFlightlineEvents: (missionId: string) => Promise<void>
    fetchEventPlannedLines: (flightPlanIds: number[]) => void
    fetchAllFlightPlans: (projectNumber: string, selectedFlightPlanIds?: number[]) => void
    updateLastSelectedEventId: (eventId: number) => void
    updateSelectedEventIds: (selectedIds: number[]) => void
    updateFlightlineEvents: (flightlineEvents: FlightlineEvent[], parseFlightlineNames?: boolean) => void
    updateSelectedFlightplans: (flightplans: FlightplanType[]) => void
    updateMatchingExpression: (matchingExpression: Partial<EventRegexExpression>) => void
    updateSortOrder: (orderBy: keyof FlightlineEvent, order: Order) => void
    updateEventStatuses: (status: FlightlineStatusOption, selectedIds: number[]) => void
    updateImportRange: (importRange: string) => void
    setInitialMatchingExpression: (expression: EventRegexTemplate) => void
    clearError: () => void
    resetState: () => void
}

const initialFlightlineEventState: FlightlineEventState = {
    sortOrder: { order: Order.asc, orderBy: "line_original_name" },
    lastSelectedEventId: -1,
    selectedEventIds: [],
    flightlineEvents: [],
    allFlightplans: [],
    selectedFlightplans: [],
    error: "",
    isFetchingEvents: false,
    isFetchingPlannedLines: false,
    isFetchingAllFlightPlans: false,
    eventPlannedLines: [],
    curRegexExpression: {
        active: regexTemplateOptions[0].value,
        custom: regexTemplateOptions[0].value,
        selectedTemplate: regexTemplateOptions[0],
        isCustom: false,
    },
    importRange: "",
}

export const useFlightlineEventStore = create<FlightlineEventState & FlightlineEventActions>()(
    persist(
        (set, get) => ({
            ...initialFlightlineEventState,
            setInitialMatchingExpression: (template) =>
                set({
                    curRegexExpression: {
                        active: template.value,
                        custom: template.value,
                        selectedTemplate: template,
                        isCustom: false,
                    },
                }),
            updateImportRange: (importRange) => set({ importRange }),
            updateSortOrder: (orderBy, order) => {
                const { flightlineEvents } = get()
                const sortOrder = { order, orderBy }

                set({
                    sortOrder,
                    flightlineEvents: sortFlightlineEvents(flightlineEvents, sortOrder),
                })
            },
            updateLastSelectedEventId: (eventId) => set({ lastSelectedEventId: eventId }),
            updateSelectedEventIds: (selectedIds) => set({ selectedEventIds: selectedIds }),
            updateMatchingExpression: (expression) => {
                const { curRegexExpression, flightlineEvents, eventPlannedLines } = get()

                const newMatchingExpression = { ...curRegexExpression, ...expression }

                newMatchingExpression.active = newMatchingExpression.isCustom
                    ? newMatchingExpression.custom
                    : newMatchingExpression.selectedTemplate?.value || ""

                let newEvents = flightlineEvents.slice()
                if (curRegexExpression.active !== newMatchingExpression.active) {
                    newEvents = parseFlightlineEventNames(
                        flightlineEvents,
                        newMatchingExpression.active,
                        eventPlannedLines,
                    )
                }

                set({ curRegexExpression: newMatchingExpression, flightlineEvents: newEvents })
            },
            updateSelectedFlightplans: (flightplans) => set({ selectedFlightplans: flightplans }),
            updateFlightlineEvents: (flightlineEvents, parseFlightlineNames) => {
                let newEvents = flightlineEvents.slice()

                if (parseFlightlineNames) {
                    const { curRegexExpression } = get()
                    newEvents = parseFlightlineEventNames(newEvents, curRegexExpression.active)
                }

                set({ flightlineEvents: newEvents })
            },
            updateEventStatuses: (status, selectedIds) => {
                const { flightlineEvents } = get()

                const newFlightlineEvents: FlightlineEvent[] = flightlineEvents.map((event) => {
                    if (selectedIds.includes(event.fe_event_id)) {
                        event.flight_status = status
                    }
                    return event
                })

                set({ flightlineEvents: newFlightlineEvents })
            },
            fetchAllFlightPlans: async (projectNumber, selectedFlightPlanIds?: number[]) => {
                set({ isFetchingAllFlightPlans: true })

                try {
                    const result: NexusSuccessResponse<FlightplanType[]> = await API.get(
                        `/flightplans/${projectNumber}`,
                        "Error retrieving flightplan list",
                    )
                    set({
                        allFlightplans: result.data,
                        isFetchingAllFlightPlans: true,
                    })
                    if (selectedFlightPlanIds != null) {
                        set({
                            selectedFlightplans: result.data.filter(
                                (flightplan) =>
                                    flightplan.flight_plan_id != null &&
                                    selectedFlightPlanIds.includes(flightplan.flight_plan_id),
                            ),
                        })
                    }
                } catch (error) {
                    log.error(error)
                    set({
                        error: `Error while fetching flightplans from project ${projectNumber}. Error message: ${error.message}`,
                        isFetchingAllFlightPlans: false,
                    })
                }
            },
            fetchEventPlannedLines: async (flightPlanIds) => {
                if (flightPlanIds.length === 0) {
                    set({ eventPlannedLines: [] })
                } else {
                    // set error to blank
                    set({ isFetchingPlannedLines: true })

                    try {
                        log.info(
                            `Fetching flight planned lines for Flight Plans: ${flightPlanIds.slice(0, 3).join(", ")}${flightPlanIds.length > 3 ? `... +${flightPlanIds.length - 3}` : ""}`,
                        )
                        const plannedLines = await getFlightPlannedLines(flightPlanIds)
                        const eventPlannedLines = getEventPlannedLines(plannedLines)

                        set({ isFetchingPlannedLines: false, eventPlannedLines })
                    } catch (error) {
                        const errorMessage = `Error fetching flight planned lines. Error message: ${error.message}.`
                        set({
                            error: errorMessage,
                            isFetchingPlannedLines: false,
                        })
                        log.error(errorMessage, error)
                    }
                }
            },
            fetchFlightlineEvents: async (missionId) => {
                set({ isFetchingEvents: true })
                const { sortOrder } = get()

                try {
                    log.info(`Fetching flightline events for mission ${missionId}`)
                    const { flightline_events, flight_plans } = await getEventsForMission(missionId)

                    // retrieve plannedLines for this mission using flight_plans just retrieved
                    const flightplanIds: number[] = flight_plans.flatMap((fp) =>
                        fp.flight_plan_id ? fp.flight_plan_id : [],
                    )

                    // Fetch flight planned lines based of retrieved flight plan ids
                    const plannedLines: CreatedFlightPlannedLine[] = await getFlightPlannedLines(flightplanIds)
                    const eventPlannedLines = getEventPlannedLines(plannedLines)

                    set({
                        flightlineEvents: sortFlightlineEvents(flightline_events, sortOrder),
                        selectedFlightplans: flight_plans,
                        eventPlannedLines,
                        isFetchingEvents: false,
                    })
                } catch (error) {
                    const errorMessage = `Error fetching flightline events from mission ${missionId}. Error message: ${error.message}`
                    set({
                        error: errorMessage,
                        isFetchingEvents: false,

                        // Clear them out so we don't see old data if there is any
                        flightlineEvents: [],
                        eventPlannedLines: [],
                    })
                    log.error(errorMessage, error)
                }
            },
            clearError: () => set({ error: "" }),
            resetState: () => {
                const { sortOrder, ...stateWithoutSortOrder } = initialFlightlineEventState
                set(stateWithoutSortOrder)
            },
        }),
        {
            name: "ZFlightlineStore",
            // We want to keep sort order in persisted
            partialize: (state) => ({ sortOrder: state.sortOrder }),
        },
    ),
)
